'use strict';

angular.module('msCore').service('providerSvc', ProviderSvc);

ProviderSvc.$inject = ['$http', '$q', '$injector', '$sessionStorage', 'errorRouter'];

function ProviderSvc($http, $q, $injector, $sessionStorage, errorRouter) {
  var organization;
  var organizationFullName;
  var group;
  var accessCode;
  var referralToken;
  var preferences = {
    getHelpText: '',
    disabledFeatures: {
      DISABLE_LOGIN_CHECK_IN_COMMENT: true
    },
    wellnessAssessments: [{
      shortName: 'DASS21',
      positionOrdinal: 1
    }],
    hasFeatureDisabled: function hasFeatureDisabled(feature) {
      return this.disabledFeatures && this.disabledFeatures[feature];
    }
  };

  function buildQueryString(organization, group, accessCode) {
    return (organization ? 'organization=' + organization + '&' : '') + (group ? 'group=' + group + '&' : '') + (accessCode ? 'accesscode=' + accessCode : '');
  }

  /**
   * Sets the cached provider context for the logged in user or the currently signing up user.
   * Calling this method adds the provider context and its preferences to localStorage
   * and provides the branding context for a user.
   * @param {Object} provider An object containing the org/group/access code
   *                          and possibly preferences for the provider.
   * @param {Boolean} disableGetPreferences Disables getPreferences() in validateProvider response.
   */
  function setProvider(provider, disableGetPreferences) {
    provider.accessCode = provider.accessCode ? provider.accessCode.trim() : '';

    // Set provider variables
    organization = provider.organization;
    group = provider.group;
    accessCode = provider.accessCode;

    // Clear out the cached preferences if explicitly set to null
    if (provider.preferences === null) {
      delete $sessionStorage.providerPreferences;
    }
    if (provider.preferences) {
      $sessionStorage.providerPreferences = angular.extend($sessionStorage.providerPreferences || {}, provider.preferences);
    }
    if (provider.referralToken) {
      referralToken = provider.referralToken;
    }
    // Prevent error if org, group, accessCode unset
    if (!organization && !group && !accessCode) {
      return $q.when({
        data: true
      });
    }
    return validateProvider(provider).then(function (resp) {
      return handleValidateProviderSuccess(resp, provider, disableGetPreferences);
    }, function (err) {
      return handleValidateProviderError(err);
    });
  }

  function handleValidateProviderSuccess(resp, provider, disableGetPreferences) {
    if (!resp || !resp.data) {
      errorRouter.navigateError({
        message: 'An unexpected error occurred',
        contact: true
      });
      return resp;
    }
    //if we set a new provider, we should clear existing preferences
    //and get them anew if that provider didn't provide them
    if (!provider.preferences) {
      delete $sessionStorage.providerPreferences;
      //kick off an async load of the provider preferences
      if (!disableGetPreferences) {
        getPreferences();
      }
    }
    return resp;
  }

  function handleValidateProviderError(err) {
    if (err.status === 404) {
      errorRouter.navigate404();
    } else {
      errorRouter.navigateError({
        errorCode: err.status,
        message: 'An unexpected error occurred',
        contact: true
      });
    }
  }

  function setOrganization(orgData) {
    organization = orgData.shortName;
    organizationFullName = orgData.name;
  }

  function validateProvider(provider) {
    var providerOrg = provider.organization;
    var providerGrp = provider.group;
    var providerAc = void 0;
    if (provider.accessCode) {
      providerAc = provider.accessCode.trim();
    }
    var queryString = buildQueryString(providerOrg, providerGrp, providerAc);
    return $http.get('/apiproxy/public/providers?' + queryString);
  }

  function getProvider() {
    return {
      organization: organization,
      organizationFullName: organizationFullName,
      group: group,
      accessCode: accessCode,
      referralToken: referralToken
    };
  }

  function acknowledgeCustomTermsOfUse() {
    $sessionStorage.agreedToCustomTermsOfUse = true;
    if ($sessionStorage.authRequirements) {
      var tosRequirement = _.find($sessionStorage.authRequirements, {
        requirementType: 'TERMS_OF_USE'
      });
      if (tosRequirement) {
        tosRequirement.complete = true;
      }
    }
  }

  function hasAgreedToCustomTermsOfUse() {
    if ($sessionStorage.authRequirements) {
      var tosRequirement = _.find($sessionStorage.authRequirements, {
        requirementType: 'TERMS_OF_USE'
      });
      if (tosRequirement) {
        return tosRequirement.complete;
      }
    }
    return false;
  }

  function getPreferences() {
    if ($sessionStorage.providerPreferences) {
      return $q.when($sessionStorage.providerPreferences);
    }

    if (!organization && !group && !accessCode) {
      return $q.when({});
    }

    var queryString = buildQueryString(organization, group, accessCode);
    return $http.get('/apiproxy/public/providers/preferences?' + queryString).then(function (response) {
      if (response && response.data) {
        if (!$sessionStorage.authRequirements) {
          $sessionStorage.authRequirements = response.data.requirements;
        } else {
          $sessionStorage.authRequirements = _.unionWith($sessionStorage.authRequirements, response.data.requirements, function (o, n) {
            return o.requirementType === n.requirementType;
          });
        }
        $sessionStorage.providerPreferences = angular.extend(preferences, response.data.preferences || {});
      }
      return $sessionStorage.providerPreferences;
    });
  }

  function isFeatureEnabled(requestedFeature) {
    return getPreferences().then(function (prefs) {
      if (angular.isArray(prefs.features)) {
        return prefs.features.filter(function (feature) {
          return feature.providerPreferenceType.name === requestedFeature && feature.flag === true;
        }).length > 0;
      }
      // Something went wrong, so default to feature disabled
      return false;
    });
  }

  //get referral settings
  function getReferralSettings() {
    var url = '/apiproxy/public/referral/settings/' + organization;
    if (accessCode || group) {
      url = url + '?' + (group ? 'group=' + group + '&' : '') + (accessCode ? 'accesscode=' + accessCode : '');
    }
    return $http.get(url).then(function (resp) {
      return resp.data;
    });
  }

  function getSpecificHeaderImage(organization, group, accessCode) {
    // fetch the co-branding
    if (organization) {
      var url = '/apiproxy/collateral/headers/organizations/' + organization;
      if (accessCode) {
        url += '/accesscodes/' + accessCode;
      } else if (group) {
        url += '/groups/' + group;
      }
      // Fetch the header (either group or org level), and return only the image.
      return $http.get(url).then(function (resp) {
        return _getImageFromHeader(resp.data);
      });
    }
  }

  function getHeader() {
    var langSvc = $injector.get('languageSvc');

    // fetch the co-branding
    if (organization) {
      var url = '/apiproxy/collateral/headers/organizations/' + organization;
      if (accessCode) {
        url += '/accesscodes/' + accessCode;
      } else if (group) {
        url += '/groups/' + group;
      }

      if (langSvc.getLanguage()) {
        url += '?language=' + langSvc.getLanguage();
      }

      // Fetch the co-branding (either group or org level)
      return $http.get(url).then(function (resp) {
        return resp.data;
      });
    } else {
      return $q.when({
        htmlTemplate: '<img class="header-left-img" src="/images/Generic-lockup-phone.svg" alt=""><div>In case of emergency please call 911 or visit an emergency location.</div>',
        content: [{
          target: 'image',
          replacement: '/images/Generic_lockup_200x50.png'
        }]
      });
    }
  }

  // Returns an array of partner logo images.
  function getAllHeaderImages() {
    return getHeader().then(function (data) {
      var contentItem;
      if (data && data.content) {
        contentItem = data.content.filter(function (headerContent) {
          return _.includes(['image', 'imageAlt'], headerContent.target);
        });
        if (contentItem) {
          return _.map(contentItem, 'replacement');
        }
      }
    });
  }

  // Filters out just the image data from an org's header.
  function _getImageFromHeader(headerData) {
    if (headerData.content) {
      return _.get(headerData.content.filter(function (headerContent) {
        return headerContent.target === 'image';
      })[0], 'replacement');
    }
  }

  function _getCoBrandingHeaderImage(headerData) {
    if (headerData.content) {
      return _.get(headerData.content.filter(function (headerContent) {
        return headerContent.target === 'coBrandHeaderImage';
      })[0], 'replacement');
    }
  }

  // Returns just the first partner logo image.
  function getHeaderImage() {
    return getHeader().then(function (data) {
      return _getImageFromHeader(data);
    });
  }

  function getCoBrandingHeaderImage() {
    return getHeader().then(function (data) {
      return _getCoBrandingHeaderImage(data);
    });
  }

  function getAccessCodeByName(accessCode) {
    return $http.get('/apiproxy/collections/accesscodes/by/code/' + accessCode);
  }

  function getAccessCodeById(accessCode) {
    return $http.get('/apiproxy/collections/accesscodes/by/id/' + accessCode);
  }

  function getAccessCodeType(accessCode) {
    return $http.get('/apiproxy/collateral/signups/access_codes/' + accessCode + '/validate');
  }

  function getOrganizationShortName() {
    return organization;
  }

  function getOrganizationFullName() {
    return organizationFullName;
  }

  return {
    getAccessCodeById: getAccessCodeById,
    getAccessCodeByName: getAccessCodeByName,
    getHeader: getHeader,
    getSpecificHeaderImage: getSpecificHeaderImage,
    getCoBrandingHeaderImage: getCoBrandingHeaderImage,
    getProvider: getProvider,
    getReferralSettings: getReferralSettings,
    getHeaderImage: getHeaderImage,
    getAllHeaderImages: getAllHeaderImages,
    getPreferences: getPreferences,
    getAccessCodeType: getAccessCodeType,
    getOrganizationShortName: getOrganizationShortName,
    getOrganizationFullName: getOrganizationFullName,
    setProvider: setProvider,
    setOrganization: setOrganization,
    validateProvider: validateProvider,
    isFeatureEnabled: isFeatureEnabled,
    hasAgreedToCustomTermsOfUse: hasAgreedToCustomTermsOfUse,
    acknowledgeCustomTermsOfUse: acknowledgeCustomTermsOfUse
  };
}